/**
 * Video grid layouts and which source each slot shows (design §7.3).
 *
 * @module
 */

export const LAYOUTS = {
  "1x1": { slots: 1, label: "1 × 1" },
  "1x2": { slots: 2, label: "1 × 2" },
  "2x2": { slots: 4, label: "2 × 2" },
  "1+3": { slots: 4, label: "1 large + 3" },
} as const;

export type Layout = keyof typeof LAYOUTS;

/** A source key per slot; null shows nothing. */
export type Slots = readonly (string | null)[];

/**
 * Fits the assignment to `count` slots and the sources there are now: keeps
 * slots whose source still exists, then fills empty ones with sources not
 * shown yet, in order.
 */
export function fill(
  prev: Slots,
  sources: readonly string[],
  count: number,
  /** Kept where they were put, but never put anywhere by themselves. */
  panels: readonly string[] = [],
): Slots {
  const known = new Set([...sources, ...panels]);
  const slots = Array.from({ length: count }, (_, i) => {
    const key = prev[i] ?? null;
    return key !== null && known.has(key) ? key : null;
  });
  const shown = new Set(slots);
  const spare = sources.filter((k) => !shown.has(k));
  return slots.map((k) => k ?? spare.shift() ?? null);
}

/** Shows `key` in slot `i`; if another slot shows it, the two swap. */
export function place(slots: Slots, i: number, key: string | null): Slots {
  const next = [...slots];
  const from = key === null ? -1 : next.indexOf(key);
  if (from >= 0) next[from] = next[i] ?? null;
  next[i] = key;
  return next;
}

/** Dropped share of frames, per source, over the last `windowMs` of samples. */
export class DropWatch {
  private readonly samples = new Map<
    string,
    { at: number; dropped: number; total: number }[]
  >();

  constructor(private readonly windowMs = 3000) {}

  /** Cumulative counts, as getVideoPlaybackQuality() reports them. */
  add(key: string, at: number, dropped: number, total: number): void {
    const list = this.samples.get(key) ?? [];
    const last = list[list.length - 1];
    if (last && total < last.total) list.length = 0;
    list.push({ at, dropped, total });
    while (list.length > 2 && (list[1]?.at ?? at) <= at - this.windowMs)
      list.shift();
    this.samples.set(key, list);
  }

  /** Null until at least `minFrames` frames were due in the window. */
  ratio(key: string, minFrames = 15): number | null {
    const list = this.samples.get(key) ?? [];
    const first = list[0];
    const last = list[list.length - 1];
    if (!first || !last) return null;
    const total = last.total - first.total;
    return total < minFrames ? null : (last.dropped - first.dropped) / total;
  }

  clear(): void {
    this.samples.clear();
  }
}

/**
 * What to do while shown videos drop frames: first slow the page's own
 * updates, which cost a weak GPU video frames whenever two or more play
 * (spike 0.1a), then pause the lowest-priority (last) shown slot. Nothing
 * while only one video is shown.
 */
export function relief(
  shown: readonly (string | null)[],
  ratio: (key: string) => number | null,
  canSlowUi: boolean,
  limits = { slowUi: 0.1, pause: 0.2 },
): "slow-ui" | { pause: number } | null {
  const active = shown.flatMap((k, i) => (k === null ? [] : [i]));
  if (active.length < 2) return null;
  const worst = Math.max(...active.map((i) => ratio(shown[i] ?? "") ?? 0));
  if (canSlowUi && worst > limits.slowUi) return "slow-ui";
  const last = active[active.length - 1];
  return worst > limits.pause && last !== undefined ? { pause: last } : null;
}
